import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformCircularMove } from "../../../transforms/transform_circular_move.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { Transformable } from "../../../transforms/transformable.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

const STAGGER_MS = 56;
const BASE_DELAY_MS = 160;
const ITEM_ANIM_DURATION_MS = 256;
const ARROW_ANIM_DURATION_MS = 280;
const TITLE_ANIM_DURATION_MS = 320;

const HOLDER_W = 982;
const HOLDER_H = 1450;

const ARROW_W = 168;
const ARROW_H = 155;

const TEXT_SIZE = 40;
const TITLE_SIZE = 80;

export class FriendsInfoOverlay extends AbstractOverlay
{
  private content_holder: UIElementHolder | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.createElements();
  }

  private createElements(): void
  {
    const base_now = performance.now() + BASE_DELAY_MS;

    const addAppearTransform = ( el: Transformable, start_time: number ): void =>
    {
      el.setOpacity( 0 );
      el.setScale( new Vector2( 0.6, 0.6 ) );
      el.addTransform( new TransformOpacity( el, true, 0, 1, start_time, ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      el.addTransform( new TransformScale( el, true, null, new Vector2( 1, 1 ), start_time, ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    };

    this.createCenteredHolder();
    this.createTopPart( base_now, addAppearTransform );
    this.createArrowTop( base_now + STAGGER_MS * 2 );
    this.createCenterPart( base_now, addAppearTransform );
    this.createArrowBottom( base_now + STAGGER_MS * 5 );
    this.createBottomPart( base_now, addAppearTransform );
    this.createTitle();
    this.createCloseButton( base_now + STAGGER_MS * 9 );
  }

  private static arcParams(
    from: Vector2,
    to: Vector2,
    bulge: number
  ): { center: Vector2; radius: number; start_angle: number; end_angle: number }
  {
    const mid     = new Vector2( ( from.x + to.x ) / 2, ( from.y + to.y ) / 2 );
    const dx      = to.x - from.x;
    const dy      = to.y - from.y;
    const len     = Math.sqrt( dx * dx + dy * dy );
    const perp_x  = -dy / len;
    const perp_y  =  dx / len;
    const center  = new Vector2( mid.x + perp_x * bulge, mid.y + perp_y * bulge );
    const radius      = Math.sqrt( ( from.x - center.x ) ** 2 + ( from.y - center.y ) ** 2 );
    const start_angle = Math.atan2( from.y - center.y, from.x - center.x );
    let   diff        = Math.atan2( to.y   - center.y, to.x   - center.x ) - start_angle;
    while ( diff >  Math.PI ) diff -= 2 * Math.PI;
    while ( diff < -Math.PI ) diff += 2 * Math.PI;
    const end_angle = start_angle + diff;
    return { center, radius, start_angle, end_angle };
  }

  private createCenteredHolder(): void
  {
    const holder_w = LayoutScaler.widthFromDesign( HOLDER_W );
    const holder_h = LayoutScaler.heightFromDesign( HOLDER_H );

    this.content_holder = new UIElementHolder(
      this.ctx,
      this.rect.getW2() - holder_w / 2,
      this.rect.getH2() - holder_h / 2,
      holder_w,
      holder_h
    );

    this.addChild( this.content_holder );
  }

  private infoText( x: number, y: number, w: number, h: number, key: string, align: MyTextAlign ): UITextElement
  {
    return new UITextElement(
      this.ctx,
      x,
      y,
      w,
      h,
      {
        localization_key: key,
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( TEXT_SIZE ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_align: align,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.17,
        ...TextStyleScheme.buttonShadow(),
      }
    );
  }

  private createTopPart( base_now: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const image = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 620 ),
      LayoutScaler.yFromDesign( 10 ),
      LayoutScaler.widthFromDesign( 330 ),
      LayoutScaler.heightFromDesign( 300 ),
      ImgID.UI_FriendsInfoTop
    );

    addAppearTransform( image, base_now );

    const text = this.infoText(
      0,
      LayoutScaler.yFromDesign( 10 ),
      LayoutScaler.widthFromDesign( 600 ),
      LayoutScaler.heightFromDesign( 300 ),
      "FRIENDS_INFO.TOP_TEXT",
      MyTextAlign.Right
    );

    addAppearTransform( text, base_now + STAGGER_MS );

    this.content_holder.addChild( text );
    this.content_holder.addChild( image );
  }

  private createCenterPart( base_now: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const image = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 10 ),
      LayoutScaler.yFromDesign( 400 ),
      LayoutScaler.widthFromDesign( 330 ),
      LayoutScaler.heightFromDesign( 350 ),
      ImgID.UI_FriendsInfoCenter
    );

    addAppearTransform( image, base_now + STAGGER_MS * 3 );
    this.content_holder.addChild( image );

    const text = this.infoText(
      LayoutScaler.xFromDesign( 370 ),
      LayoutScaler.yFromDesign( 400 ),
      LayoutScaler.widthFromDesign( 600 ),
      LayoutScaler.heightFromDesign( 350 ),
      "FRIENDS_INFO.CENTER_TEXT",
      MyTextAlign.Left
    );

    addAppearTransform( text, base_now + STAGGER_MS * 4 );
    this.content_holder.addChild( text );
  }

  private createBottomPart( base_now: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const holder_w = LayoutScaler.widthFromDesign( HOLDER_W );
    const image_w = LayoutScaler.widthFromDesign( 730 );

    const image = new UIImage(
      this.ctx,
      ( holder_w - image_w ) / 2,
      LayoutScaler.yFromDesign( 830 ),
      image_w,
      LayoutScaler.heightFromDesign( 414 ),
      ImgID.UI_FriendsInfoBottom
    );

    addAppearTransform( image, base_now + STAGGER_MS * 6 );
    this.content_holder.addChild( image );

    const text_w = LayoutScaler.widthFromDesign( 900 );

    const text = this.infoText(
      ( holder_w - text_w ) / 2,
      LayoutScaler.yFromDesign( 1250 ),
      text_w,
      LayoutScaler.heightFromDesign( 200 ),
      "FRIENDS_INFO.BOTTOM_TEXT",
      MyTextAlign.Center
    );

    addAppearTransform( text, base_now + STAGGER_MS * 7 );
    this.content_holder.addChild( text );
  }

  private createArrow( start_time: number, from: Vector2, to: Vector2, bulge: number, rotation_from_deg: number, rotation_to_deg: number, mirrored: boolean ): void
  {
    if ( !this.content_holder ) return;

    const from_rad = rotation_from_deg * Math.PI / 180;
    const to_rad = rotation_to_deg * Math.PI / 180;

    const arrow = new UIImage(
      this.ctx,
      from.x,
      from.y,
      LayoutScaler.widthFromDesign( ARROW_W ),
      LayoutScaler.heightFromDesign( ARROW_H ),
      ImgID.UI_DailyTasksArrow,
      mirrored ? { direction_x: -1, direction_y: 1 } : undefined
    );

    arrow.setOpacity( 0 );
    arrow.setRotation( from_rad );

    const arc = FriendsInfoOverlay.arcParams( from, to, bulge );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, from_rad, to_rad, start_time, ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc.center, arc.radius, start_time, ARROW_ANIM_DURATION_MS, arc.start_angle, arc.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createArrowTop( start_time: number ): void
  {
    this.createArrow(
      start_time,
      new Vector2( LayoutScaler.xFromDesign( 785 ), LayoutScaler.yFromDesign( 160 ) ),
      new Vector2( LayoutScaler.xFromDesign( 520 ), LayoutScaler.yFromDesign( 320 ) ),
      LayoutScaler.widthFromDesign( 120 ),
      182,
      137,
      true
    );
  }

  private createArrowBottom( start_time: number ): void
  {
    this.createArrow(
      start_time,
      new Vector2( LayoutScaler.xFromDesign( 175 ), LayoutScaler.yFromDesign( 575 ) ),
      new Vector2( LayoutScaler.xFromDesign( 190 ), LayoutScaler.yFromDesign( 760 ) ),
      LayoutScaler.widthFromDesign( -120 ),
      85,
      60,
      false
    );
  }

  private createTitle(): void
  {
    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( 128 );
    const title_x = this.rect.getW2() - title_w / 2;
    const title_y = LayoutScaler.yFromDesign( 94 );
    const title_offset_y = LayoutScaler.yFromDesign( 20 );
    const now = performance.now();

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "FRIENDS.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( TITLE_SIZE ),
        min_font_size: LayoutScaler.textSizeFromDesign( 48 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.friends_title_outline,
        auto_wrap: false,
        fit_font_size: true,
      }
    );

    title.setOpacity( 0 );
    title.setPixelPos( new Vector2( title_x, title_y + title_offset_y ), true );
    title.addTransform( new TransformOpacity( title, true, 0, 1, now, TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformStraightMove( title, true, null, new Vector2( title_x, title_y ), now, TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );

    this.addChild( title );
  }

  private createCloseButton( start_time: number ): void
  {
    const size = LayoutScaler.widthFromDesign( 130 );
    const bottom_margin = LayoutScaler.yFromDesign( 95 );

    const close = new UIImage(
      this.ctx,
      this.rect.getW2() - size / 2,
      this.rect.getH() - bottom_margin - size,
      size,
      size,
      ImgID.UI_DailyTasksCloseBtn,
      undefined,
      true
    );

    close.setOpacity( 0 );
    close.addTransform( new TransformOpacity( close, true, 0, 1, start_time, ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    close.setOnClick( () => OverlayManager.closeOverlay() );

    this.addChild( close );
  }

  public getDimOpacity(): number
  {
    return 0.85;
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    OverlayManager.closeOverlay();
    return true;
  }

  public update( now: number ): void
  {
    super.update( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "FriendsInfoOverlay";
  }
}
